import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { MaterialGroup, RecyclingCategory } from '@trp/nationaldb/interfaces';
import {
  Observable,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  retry,
  switchMap,
  tap, forkJoin, take, combineLatest,
} from 'rxjs';

function selectOnce<T>(obs$: Observable<T>) {
  return firstValueFrom(obs$.pipe(take(1)));
}

@Injectable({
  providedIn: 'root',
})
export class RecyclingCategoriesDataAccessClient {
  private store = new ComponentStore<{
    recyclingCategories: RecyclingCategory[] | null;
    requestKey: string | null;
  }>({ recyclingCategories: null, requestKey: null });


  private initPromise: Promise<RecyclingCategory[]> | null = null;
  private initKey: string | null = null;


  private currentKey$ = this.store.select(s => s.requestKey).pipe(distinctUntilChanged());

  definedRecyclingCategories$ = combineLatest([
    this.store.select(s => s.recyclingCategories),
    this.currentKey$
  ]).pipe(
      filter(([cats, key]) => !!cats && !!key),
      map(([cats]) => cats as RecyclingCategory[]),
      distinctUntilChanged()
  );

  fetch = this.store.effect((trigger$: Observable<{ key: string }>) =>
      trigger$.pipe(
          tap(({ key }) => this.store.patchState({
            requestKey: key,
            recyclingCategories: null,
          })),
          switchMap(() =>
              this.httpClient.get<RecyclingCategory[]>('/recycling-categories').pipe(
                  retry({ delay: 2000 }),
                  tap(cats => this.store.patchState({ recyclingCategories: cats }))
              )
          )
      )
  );

  fetchForState = this.store.effect((trigger$: Observable<{ state: string, key: string }>) =>
      trigger$.pipe(
          tap(({ key }) => this.store.patchState({
            requestKey: key,
            recyclingCategories: null,
          })),
          switchMap(({ state }) =>
              forkJoin({
                recyclingCategories: this.httpClient.get<RecyclingCategory[]>('/recycling-categories'),
                recyclingStateList: this.httpClient.get<any[]>(`/recycling-categories/state-list?state=${state}`)
              }).pipe(
                  retry({ delay: 2000 }),
                  tap(({ recyclingCategories, recyclingStateList }) => {
                    const withTags = recyclingCategories.map(cat => {
                      const tags = recyclingStateList.filter(x => x.indicated_recycling_category_ids.includes(cat.id));
                      return { ...cat, tags };
                    });
                    this.store.patchState({ recyclingCategories: withTags })
                  })
              )
          )
      )
  );

  private setRecyclingCategories = this.store.updater(
    (state, recyclingCategories: RecyclingCategory[]) => ({
      ...state,
      recyclingCategories,
    })
  );

  constructor(private httpClient: HttpClient) {}

  initialize(state?: string): Promise<RecyclingCategory[]> {
    const key = state ? `state:${state}` : 'all';

    return (async () => {
      const snap = await selectOnce(this.store.select(s => s.recyclingCategories));
      const snapKey = await selectOnce(this.store.select(s => s.requestKey));

      if (snap && snapKey === key) return snap;

      if (this.initPromise && this.initKey === key) return this.initPromise;

      this.initKey = key;

      if (state) this.fetchForState({ state, key });
      else this.fetch({ key });

      this.initPromise = firstValueFrom(
          this.store.select(s => s).pipe(
              filter(s => !!s.recyclingCategories && s.requestKey === key),
              map(s => s.recyclingCategories as RecyclingCategory[])
          )
      ).finally(() => {
        this.initPromise = null;
        this.initKey = null;
      });

      return this.initPromise;
    })();
  }

  groupMaterialsByCategory$(materials: number[]): Observable<MaterialGroup[]> {
    return this.definedRecyclingCategories$.pipe(
      map((recyclingCategories) => {
        let materialGroups = materials.reduce((prev, curr) => {
          const category = recyclingCategories.find((c) => c.id === curr);
          if (!category) {
            // console.warn('Recycling category not found for id ' + curr);
            return prev;
          }
          const existingMaterialGroup = prev.find(
            (p) => p.material === category.material
          );
          if (!existingMaterialGroup) {
            const newMaterialGroup: MaterialGroup = {
              material: category.material,
              categories: [
                {
                  name: category.subcategory || '',
                  items: [category],
                },
              ],
            };
            return [...prev, newMaterialGroup];
          }
          const existingSubcategoryGroup =
            existingMaterialGroup.categories.find(
              (c) => c.name === (category.subcategory || '')
            );
          if (!existingSubcategoryGroup) {
            existingMaterialGroup.categories.push({
              name: category.subcategory || '',
              items: [category],
            });
            existingMaterialGroup.categories.sort((a, b) =>
              a.name.localeCompare(b.name)
            );
          } else {
            existingSubcategoryGroup.items.push(category);
            existingSubcategoryGroup.items.sort((a, b) =>
              a.category.localeCompare(b.category)
            );
          }
          return prev;
        }, [] as MaterialGroup[]);

        materialGroups = materialGroups.map((item) => {
          // Move Other Plastic to the bottom of the list
          if (item.material === 'Plastic') {
            const found = item.categories.find(
              (c) => c.name === 'Other Plastic'
            );

            if (found) {
              item.categories = [
                ...item.categories.filter((c) => c.name !== 'Other Plastic'),
                found,
              ];
            }
          }

          // Remove category name for Metal and Glass
          if (item.material === 'Metal' || item.material === 'Glass') {
            item.categories = item.categories.map((category) => ({
              ...category,
              name: '',
            }));
          }

          return item;
        });

        return materialGroups;
      })
    );
  }
}
